import React, { useEffect, useLayoutEffect, useState } from 'react'
import { CssBaseline, ThemeProvider, Theme, Stack } from '@mui/material'
import { useDispatch, useSelector } from 'react-redux'

import Env from '../../shared/config/envConfig'
import appConfig from '../../shared/config/appConfig'
import editConfig from '../../shared/config/editConfig'
import { getAppTheme } from '../../shared/config/getAppTheme'
import SnackbarComponent from '../../shared/components/MessageComponents/SnackbarComponent'
import { getCrudeAppName, getPrefersDarkMode, getRedirectPath, getUser } from '../../store/settings/settings.selectors'
import { Route, Switch, useLocation } from 'react-router'
import TableActionButtons from './TableActionButtons'

// Import CRUDE
import { CrudeConfiguration } from '@3m5/crude-frontend/dist/shared/types/configurationTypes'
import { useCrude, CrudeContext } from '@3m5/crude-frontend/dist/crudeHooks'
import { crudeRootReducer } from '@3m5/crude-frontend/dist/store/crudeRoot.reducer'

import Login from '../../shared/components/Login'
import { useHistory } from 'react-router-dom'
import { setEventSettings, setUser } from '../../store/settings/settings.actions'
import HeaderComponent from './Header/HeaderComponent'
import Settings from './Settings'
import { createStore } from 'redux'
import crudeTabConfig from '../../shared/config/crudeTabConfig'
import Dashboard from './Dashboard'
import Email from './Email'
import Download from './Download'
import Confirmation from './Confirmation'
import SetPassword from './SetPassword'
import Registration from './Registration'
import ThankYou from './ThankYou'
import Checkin from './Checkin'
import AdminPlan from './AdminPlan'
import AdminPayments from './AdminPayments'
import { addCsrfHeaders, CSRF_HEADER, getCsrfToken } from '../../shared/config/csrf'
import crudeLicense from '../../shared/config/crudeLicense'

if ('scrollRestoration' in window.history) {
  window.history.scrollRestoration = 'manual'
}

const ScrollToTop = () => {
  const { pathname } = useLocation()
  useLayoutEffect(() => {
    document.documentElement.scrollTop = 0
    document.body.scrollTop = 0
    window.scrollTo(0, 0)
  }, [pathname])
  return null
}

const DashboardApp = () => <Dashboard />
const SettingsApp = () => <Settings />
const EmailApp = () => <Email />
const DownloadApp = () => <Download />
const CheckinApp = () => <Checkin />
const RegistrationApp = () => <Registration />
const ThankYouApp = () => <ThankYou />
const AdminPlanApp = () => <AdminPlan />
const AdminPaymentsApp = () => <AdminPayments />

const App: React.FC = () => {
  const dispatch = useDispatch()
  const history = useHistory()

  const store2 = createStore(
    crudeRootReducer,
  )

  const user = useSelector(getUser)
  const darkMode = useSelector(getPrefersDarkMode)
  const redirectPath = useSelector(getRedirectPath)

  const [theme, setTheme] = useState<Theme>(getAppTheme(darkMode))
  const appName: string = useSelector(getCrudeAppName)

  useEffect(() => {
    const oldMode = localStorage.getItem('darkMode') === 'true'
    store2.getState().crudeStore.darkMode = darkMode
    setTheme(getAppTheme(darkMode))
    if (oldMode !== darkMode) {
      localStorage.setItem('darkMode', darkMode ? 'true' : 'false')
      location.reload()
    }
  },
  [darkMode],
  )

  const [token, setToken] = useState<string | null>(null)
  const [passwordToken, setPasswordToken] = useState<string | null>(null)

  useEffect(() => {
    fetch(`${Env.basePath}/api/settings`)
      .then(res => res.json())
      .then(data => {
        dispatch(setEventSettings(data))
      })
      .catch(err => console.error('Failed to fetch settings:', err))
  }, [dispatch])

  useEffect(() => {
    const actualPath = history.location.pathname
    setToken(null)
    setPasswordToken(null)
    let pathname = '/'

    if (actualPath.search('confirmation') !== -1) {
      pathname = 'confirmation'
    } else if (actualPath.search('set-password') !== -1) {
      pathname = 'set-password'
    } else if (actualPath.search('checkin') !== -1) {
      pathname = 'checkin'
    } else {
      if (actualPath.search('registration') !== -1) {
        pathname = 'registration'
      } else {
        if (actualPath.search('thankyou') !== -1) {
          pathname = 'thankyou'
        } else {
          if (!user) {
            pathname = 'login'
          } else {
            if (redirectPath) {
              history.push(redirectPath)
            }
          }
        }
      }
    }

    switch (pathname) {
      case 'login':
        history.push('/login')
        break
      case 'confirmation':
        setToken(history.location.pathname.replace('confirmation/', '').replace('/', ''))
        break
      case 'set-password':
        setPasswordToken(history.location.pathname.replace(/.*set-password\//, '').replace(/\/$/, ''))
        break
      case 'checkin':
        // No token needed for checkin, handled via query params in component
        break
      case 'registration':
        history.push('/registration')
        break
      case 'thankyou':
        history.push('/thankyou')
        break
      default:
        history.push('/')
        break
    }
  }, [history, user],
  )

  const crudeConfiguration = {
    theme: theme,
    path: `/${appName}`,
    api: {
      app: appName,
      restPath: `${Env.crudeRestBase}/api/crude/`,
      headers: [
        {
          name: CSRF_HEADER,
          getHeader: (): string => getCsrfToken(),
        },
      ],
    },
    appLayout: appConfig(),
    editLayout: editConfig(),
    get tabs () { return crudeTabConfig() },
    crudeLicense: crudeLicense(),
  } as CrudeConfiguration

  const basePath = Env.basePath
  const requestHeaders: HeadersInit = new Headers()
  requestHeaders.set('Accept', 'application/json')
  requestHeaders.set('Content-Type', 'application/json')
  addCsrfHeaders(requestHeaders)
  const onLogout = () => {
    const restUrl = `${basePath}/api/session/logout`
    fetch(restUrl,
      {
        method: 'POST',
        credentials: 'include',
        headers: requestHeaders,
      })
      .then(() => dispatch(setUser(undefined)))
      .catch(err => console.log(err))
  }

  useEffect(() => {
    const restUrl = `${basePath}/api/session/user`
    fetch(restUrl,
      {
        method: 'GET',
        credentials: 'include',
        headers: requestHeaders,
      })
      .then(res => res.json())
      .then(json => {
        !json.error && dispatch(setUser(json))
      })
      .catch(err => console.log(err))
  },
  [],
  )

  window.addEventListener('CrudeLogout', (() => {
    onLogout()
  }) as EventListener)

  const ConfirmationApp = () => (<Confirmation token={token} />)
  const SetPasswordApp = () => (<SetPassword token={passwordToken} />)

  const crudeContext = useCrude({ appPageProps: crudeConfiguration, tableActionComponent: TableActionButtons })
  return (
    <>
      <CrudeContext.Provider value={crudeContext}>
        <ThemeProvider theme={theme}>
          <CssBaseline />
          <ScrollToTop />
          <Stack spacing={2} sx={{ width: '100%', padding: { xs: '0 5px', sm: '0 20px' } }}>
            <div className='main'>
              <HeaderComponent doLogout={() => onLogout()} />
              <Switch>
                <Route exact path='/login' component={Login} />
                <Route exact path='/registration' component={RegistrationApp} />
                <Route exact path='/thankyou' component={ThankYouApp} />
                <Route exact path='/checkin/:kennung?' component={CheckinApp} />
                {token && <Route path='/confirmation/:token?' component={ConfirmationApp} />}
                {passwordToken && <Route path='/set-password/:token?' component={SetPasswordApp} />}
                {user && !token &&
                  <>
                    <Route exact path='/' component={DashboardApp} />
                    <Route exact path='/settings' component={SettingsApp} />
                    <Route exact path='/admin-plan' component={AdminPlanApp} />
                    <Route exact path='/admin-payments' component={AdminPaymentsApp} />
                    <Route exact path='/email' component={EmailApp} />
                    <Route exact path='/download' component={DownloadApp} />
                    <Route key='crude' path={`/${appName}`} component={crudeContext.Crude} />
                  </>}
              </Switch>
            </div>
            {TableActionButtons}
            <SnackbarComponent />
          </Stack>
        </ThemeProvider>
      </CrudeContext.Provider>
    </>
  )
}

export default App
